import { useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, ListItemText, MenuItem, Table, TableBody, TableHead, TableRow, Tooltip } from '@mui/material';
import { Add as AddIcon, AutoFixHigh, Payments } from '@mui/icons-material';
import {
  ActionIconButton,
  CustomButton,
  CustomSelect,
  CustomTextField,
  GeneralTableBox,
  HeadTableCell,
  HorizontalForm,
  NoDataSkeleton,
  PaginationTable,
  RowTableCell,
  StatusChip,
  TableBodySkeleton,
} from '../../../common/components';
import {
  useSupplierInvoiceAiStatus,
  useSupplierInvoiceAttachmentSignedUrl,
  useSupplierInvoices,
} from '../../../hooks/useSupplierLedger';
import { INVOICE_DISPLAY_STATUS_LABELS, compactFormSx, statusColor, tabPanelSx } from '../constants/supplierLedgerUiConstants';
import {
  getGeneratedPaymentOrderTooltip,
  getInvoiceDisplayStatus,
  hasGeneratedPaymentOrder,
  isInvoiceSelectableForPaymentOrder,
} from '../utils/supplierLedgerHelpers';
import { formatAmount } from '../utils/supplierLedgerFormatters';
import useClientPagination from '../hooks/useClientPagination';
import { ClassificationChips } from './SupplierLedgerShared';
import CancelInvoiceDialog from './CancelInvoiceDialog';
import CreatePaymentOrderDialog from './CreatePaymentOrderDialog';
import SupplierInvoiceDialog from './SupplierInvoiceDialog';

const dateFilterSx = {
  width: 154,
  flex: '0 0 154px',
  mt: 0,
  '& .MuiOutlinedInput-root': {
    height: 56,
  },
  '& .MuiInputBase-input[type="date"]': {
    fontSize: 16,
    paddingLeft: '12px',
    paddingRight: '6px',
    '&::-webkit-calendar-picker-indicator': {
      marginLeft: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
};

const statusFilterSx = {
  width: 216,
  flex: '0 0 216px',
  mt: 0,
};

const InvoicesTab = ({ providers, providerMap }) => {
  const [filters, setFilters] = useState({
    invoice_start_date: '',
    invoice_end_date: '',
    due_start_date: '',
    due_end_date: '',
    statuses: [],
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [invoiceToEdit, setInvoiceToEdit] = useState(null);
  const [selected, setSelected] = useState({});
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState(null);
  const apiFilters = useMemo(() => ({
    invoice_start_date: filters.invoice_start_date || undefined,
    invoice_end_date: filters.invoice_end_date || undefined,
    due_start_date: filters.due_start_date || undefined,
    due_end_date: filters.due_end_date || undefined,
  }), [filters.due_end_date, filters.due_start_date, filters.invoice_end_date, filters.invoice_start_date]);
  const { data: invoices = [], isLoading } = useSupplierInvoices(apiFilters);
  const { data: aiStatus } = useSupplierInvoiceAiStatus();
  const attachmentSignedUrl = useSupplierInvoiceAttachmentSignedUrl();

  const filteredInvoices = useMemo(() => {
    const selectedStatuses = new Set(filters.statuses || []);
    if (!selectedStatuses.size) return invoices;
    return invoices.filter((invoice) => selectedStatuses.has(getInvoiceDisplayStatus(invoice)));
  }, [filters.statuses, invoices]);

  const payableInvoices = useMemo(
    () => filteredInvoices.filter(isInvoiceSelectableForPaymentOrder),
    [filteredInvoices],
  );
  const payableInvoiceIds = useMemo(
    () => new Set(payableInvoices.map((invoice) => invoice.uuid)),
    [payableInvoices],
  );
  const selectedInvoices = payableInvoices.filter((invoice) => selected[invoice.uuid]);
  const canCreateOrder = selectedInvoices.length > 0;
  const invoicePagination = useClientPagination(filteredInvoices);
  const statusOptions = useMemo(
    () => Object.entries(INVOICE_DISPLAY_STATUS_LABELS).map(([value, label]) => ({ value, label })),
    [],
  );
  const statusLabelsByValue = useMemo(
    () => Object.fromEntries(statusOptions.map((option) => [option.value, option.label])),
    [statusOptions],
  );
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };
  const handleStatusFilterChange = (event) => {
    const { value } = event.target;
    setFilters((prev) => ({
      ...prev,
      statuses: typeof value === 'string' ? value.split(',') : value,
    }));
  };
  const handleCloseInvoiceDialog = () => {
    setDialogOpen(false);
    setInvoiceToEdit(null);
  };

  const handleOpenAttachment = async (invoice) => {
    const attachment = invoice?.attachments?.[0];
    if (!attachment) return;
    try {
      const response = await attachmentSignedUrl.mutateAsync({
        invoiceId: invoice.uuid,
        attachmentId: attachment.uuid,
      });
      if (response?.signed_url) {
        window.open(response.signed_url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Error al abrir adjunto:', err);
    }
  };

  useEffect(() => {
    setSelected((prev) => {
      let changed = false;
      const next = {};

      Object.entries(prev).forEach(([uuid, isSelected]) => {
        if (isSelected && payableInvoiceIds.has(uuid)) {
          next[uuid] = true;
          return;
        }
        changed = true;
      });

      return changed ? next : prev;
    });
  }, [payableInvoiceIds]);

  return (
    <Box sx={tabPanelSx}>
      <HorizontalForm withBorder={false} sx={{ ...compactFormSx, alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', flex: '0 0 auto' }}>
          <CustomButton
            variant="base"
            startIcon={<AddIcon />}
            onClick={() => {
              setInvoiceToEdit(null);
              setDialogOpen(true);
            }}
          >
            Cargar factura
          </CustomButton>
          <CustomButton variant="outlined" startIcon={<AutoFixHigh />} disabled>
            {aiStatus?.message || 'Lectura IA próximamente'}
          </CustomButton>
          <CustomButton variant="outlined" startIcon={<Payments />} disabled={!canCreateOrder} onClick={() => setOrderDialogOpen(true)}>
            Generar OP
          </CustomButton>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: '1 1 880px',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'flex-start',
            justifyContent: { xs: 'flex-start', lg: 'flex-end' },
            minWidth: 0,
          }}
        >
          <CustomTextField label="Factura desde" type="date" name="invoice_start_date" value={filters.invoice_start_date} onChange={handleFilterChange} shrinkLabel sx={dateFilterSx} />
          <CustomTextField label="Factura hasta" type="date" name="invoice_end_date" value={filters.invoice_end_date} onChange={handleFilterChange} shrinkLabel sx={dateFilterSx} />
          <CustomTextField label="Venc. desde" type="date" name="due_start_date" value={filters.due_start_date} onChange={handleFilterChange} shrinkLabel sx={dateFilterSx} />
          <CustomTextField label="Venc. hasta" type="date" name="due_end_date" value={filters.due_end_date} onChange={handleFilterChange} shrinkLabel sx={dateFilterSx} />
          <CustomSelect
            label="Estados"
            name="statuses"
            value={filters.statuses}
            onChange={handleStatusFilterChange}
            multiple
            displayEmpty
            renderValue={(selected) => {
              if (!selected.length) return 'Todos';
              return selected.map((status) => statusLabelsByValue[status] || status).join(', ');
            }}
            sx={statusFilterSx}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox size="small" checked={filters.statuses.includes(option.value)} />
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </CustomSelect>
        </Box>
      </HorizontalForm>
      <GeneralTableBox>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <HeadTableCell padding="checkbox" />
              <HeadTableCell>Fecha</HeadTableCell>
              <HeadTableCell>Proveedor</HeadTableCell>
              <HeadTableCell>Comprobante</HeadTableCell>
              <HeadTableCell>Concepto</HeadTableCell>
              <HeadTableCell>Clasificación</HeadTableCell>
              <HeadTableCell>Adjuntos</HeadTableCell>
              <HeadTableCell>Moneda</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Total ARS</HeadTableCell>
              <HeadTableCell align="right" sx={{ textAlign: 'right' }}>Pendiente ARS</HeadTableCell>
              <HeadTableCell>Estado</HeadTableCell>
              <HeadTableCell>Acciones</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodySkeleton />
            ) : filteredInvoices.length ? invoicePagination.paginatedRows.map((invoice) => {
              const hasGeneratedOrder = hasGeneratedPaymentOrder(invoice);
              const isSelectable = isInvoiceSelectableForPaymentOrder(invoice);
              const canCancel = invoice.status === 'open' && Number(invoice.paid_amount || 0) === 0;
              const canEdit = ['open', 'partially_paid'].includes(invoice.status);
              const displayStatus = getInvoiceDisplayStatus(invoice);
              return (
                <TableRow key={invoice.uuid} hover>
                  <RowTableCell padding="checkbox">
                    <Checkbox
                      checked={isSelectable && Boolean(selected[invoice.uuid])}
                      disabled={!isSelectable}
                      onChange={(event) => {
                        const { checked } = event.target;
                        setSelected((prev) => {
                          const next = { ...prev };
                          if (checked) {
                            next[invoice.uuid] = true;
                          } else {
                            delete next[invoice.uuid];
                          }
                          return next;
                        });
                      }}
                    />
                  </RowTableCell>
                  <RowTableCell>{invoice.invoice_date}</RowTableCell>
                  <RowTableCell>{providerMap[invoice.uuid_legal_entity] || invoice.provider_name || '-'}</RowTableCell>
                  <RowTableCell>{invoice.receipt_type} {invoice.receipt_number}</RowTableCell>
                  <RowTableCell>{invoice.concept_summary || '-'}</RowTableCell>
                  <RowTableCell><ClassificationChips row={invoice} /></RowTableCell>
                  <RowTableCell>
                    {invoice.attachments?.length ? (
                      <Tooltip title={`Ver ${invoice.attachments[0].original_filename || 'adjunto'}`}>
                        <span>
                          <ActionIconButton
                            variant="download"
                            onClick={() => handleOpenAttachment(invoice)}
                            disabled={attachmentSignedUrl.isPending}
                          />
                        </span>
                      </Tooltip>
                    ) : '-'}
                  </RowTableCell>
                  <RowTableCell>{invoice.currency || 'ARS'}</RowTableCell>
                  <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(invoice.total_amount_ars)}</RowTableCell>
                  <RowTableCell align="right" sx={{ textAlign: 'right' }}>{formatAmount(invoice.pending_amount_ars)}</RowTableCell>
                  <RowTableCell>
                    <StatusChip
                      size="small"
                      color={statusColor[displayStatus] || 'default'}
                      label={INVOICE_DISPLAY_STATUS_LABELS[displayStatus] || displayStatus}
                      tooltip={hasGeneratedOrder ? getGeneratedPaymentOrderTooltip(invoice) : undefined}
                    />
                  </RowTableCell>
                  <RowTableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Tooltip title={canEdit ? (hasGeneratedOrder ? 'Editar datos descriptivos y clasificación' : 'Editar factura') : 'Sólo se pueden editar facturas pendientes'}>
                        <span>
                          <ActionIconButton
                            variant="edit"
                            disabled={!canEdit}
                            onClick={() => {
                              setInvoiceToEdit(invoice);
                              setDialogOpen(true);
                            }}
                          />
                        </span>
                      </Tooltip>
                      <Tooltip title={canCancel ? (hasGeneratedOrder ? 'Anular factura y OP asociada' : 'Anular factura') : 'Sólo se pueden anular facturas abiertas sin pagos'}>
                        <span>
                          <ActionIconButton
                            variant="delete"
                            disabled={!canCancel}
                            onClick={() => setInvoiceToCancel(invoice)}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  </RowTableCell>
                </TableRow>
              );
            }) : (
              <NoDataSkeleton colSpan={12} message="No hay facturas de proveedores con esos filtros." />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>
      {!!filteredInvoices.length && (
        <PaginationTable
          page={invoicePagination.page}
          totalPages={invoicePagination.totalPages}
          totalCount={invoicePagination.totalCount}
          rowsPerPage={invoicePagination.rowsPerPage}
          onPageChange={invoicePagination.handlePageChange}
          onRowsPerPageChange={invoicePagination.handleRowsPerPageChange}
        />
      )}
      <SupplierInvoiceDialog
        open={dialogOpen}
        onClose={handleCloseInvoiceDialog}
        providers={providers}
        invoiceToEdit={invoiceToEdit}
      />
      <CreatePaymentOrderDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        invoices={selectedInvoices}
        providerMap={providerMap}
      />
      <CancelInvoiceDialog
        open={Boolean(invoiceToCancel)}
        invoice={invoiceToCancel}
        providerName={invoiceToCancel ? providerMap[invoiceToCancel.uuid_legal_entity] : ''}
        onClose={() => setInvoiceToCancel(null)}
      />
    </Box>
  );
};

export default InvoicesTab;
